import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageSquare, 
  X, 
  Send, 
  Sparkles, 
  ArrowUpRight,
  HelpCircle,
  BarChart3,
  Lightbulb,
  CheckCircle2,
  Layers,
  ChevronRight,
  TrendingUp,
  Info,
  AlertTriangle,
  ArrowRight,
  User,
  ShoppingBag,
  TrendingDown,
  Clock,
  CheckCircle,
  Activity,
  ArrowDownRight
} from 'lucide-react';
import { ChatMessage, ChatWidget } from '../types';
import { chatSuggestions } from '../data/mockData';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip as ChartTooltip, 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  CartesianGrid
} from 'recharts';

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      sender: 'bot',
      text: "Hello! I am your Nooe Decision Assistant. Ask me anything about your Q2 metrics, customer cohorts, marketing spend, or process optimization opportunities.",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      widgets: [
        {
          type: 'kpi',
          title: 'De-duplicated Combined Revenue',
          kpiData: { value: '₹40.4L', change: '+14% MoM', trend: 'up' }
        },
        {
          type: 'kpi',
          title: 'Blended Advertising ROAS',
          kpiData: { value: '3.39', change: '+8.2%', trend: 'up' }
        },
        {
          type: 'recommendation',
          recommendationData: {
            title: 'Welcome to Nooe Decision Copilot',
            desc: 'Select any of the suggested prompts below or ask a custom question. All answers are visualized instantly inside this prototype workspace with zero loading delay.',
            priority: 'medium'
          }
        }
      ]
    }
  ]);
  const [input, setInput] = useState('');
  const [activeMessageId, setActiveMessageId] = useState<string>('welcome');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Dynamic Follow-up Suggestions based on the active topic
  const getFollowUpQuestions = (text: string): string[] => {
    const lowercase = text.toLowerCase();
    if (lowercase.includes("revenue") || lowercase.includes("sales") || lowercase.includes("combined") || lowercase.includes("de-duplicate")) {
      return [
        "What are DTC vs B2B monthly trends?",
        "Show Shopify order de-duplication details",
        "How can we optimize Zoho books synchronization?",
        "What is the projected revenue for next month?",
        "Ask a custom question..."
      ];
    }
    if (lowercase.includes("conversion") || lowercase.includes("funnel") || lowercase.includes("drop")) {
      return [
        "How can we fix checkout page drop-offs?",
        "Why does Page Views to ATC drop so much?",
        "Can we run an A/B test on checkout UPI?",
        "Show conversion by customer cohort",
        "Ask a custom question..."
      ];
    }
    if (lowercase.includes("stock") || lowercase.includes("inventory") || lowercase.includes("oos") || lowercase.includes("available")) {
      return [
        "Which desk mat colors are OOS?",
        "What is the estimated lost sales value?",
        "Can we run a supplier lead time report?",
        "How can we optimize safety buffer stock?",
        "Ask a custom question..."
      ];
    }
    if (lowercase.includes("ads") || lowercase.includes("meta") || lowercase.includes("google") || lowercase.includes("roas") || lowercase.includes("marketing") || lowercase.includes("spend")) {
      return [
        "Show ROAS of Meta vs Google Search",
        "What are our top performing ad creatives?",
        "How can we allocate Google budget to Meta?",
        "What is the CPP target for next campaign?",
        "Ask a custom question..."
      ];
    }
    if (lowercase.includes("owner") || lowercase.includes("neetica") || lowercase.includes("trisha") || lowercase.includes("portfolio")) {
      return [
        "What is Neetica's top selling category?",
        "What is Trisha's active SKU count?",
        "Compare desk mat margins vs tech organizers",
        "How do we balance portfolio resource split?",
        "Ask a custom question..."
      ];
    }
    if (lowercase.includes("cx") || lowercase.includes("escalation") || lowercase.includes("automation") || lowercase.includes("csat") || lowercase.includes("delivery")) {
      return [
        "How many weekly manual delivery cases?",
        "How do we link Asana to Shiprocket?",
        "What is Pragatee's CSAT target?",
        "Show stitching pouch Return Prime details",
        "Ask a custom question..."
      ];
    }
    // Default fallback follow-ups
    return [
      "Show Q2 Combined Revenue details",
      "Why is storefront conversion dropping?",
      "Which items are out-of-stock (OOS)?",
      "Break down Meta & Google Ads spend",
      "How are Neetica & Trisha portfolios split?"
    ];
  };

  // Zero-latency response engine that simulates intelligence with instant visual assets
  const handleSend = (textToSend: string) => {
    if (!textToSend.trim() || textToSend === "Ask a custom question...") return;

    const userMsgId = Math.random().toString();
    const userMsg: ChatMessage = {
      id: userMsgId,
      sender: 'user',
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    const lowercaseMsg = textToSend.toLowerCase();
    let botResponseText = "";
    let botWidgets: ChatWidget[] = [];

    // Intelligence router for prototype
    if (lowercaseMsg.includes("revenue") || lowercaseMsg.includes("sales") || lowercaseMsg.includes("combined") || lowercaseMsg.includes("de-duplicate")) {
      botResponseText = "Here is Nooe's Q2 combined revenue breakdown, correctly accounting for ₹4.8L of assisted Shopify orders de-duplication:";
      botWidgets = [
        {
          type: 'kpi',
          title: 'De-duplicated Combined Revenue',
          kpiData: { value: '₹40.4L', change: '+14.3% MoM', trend: 'up' }
        },
        {
          type: 'chart',
          title: 'Monthly Sales Channels (DTC vs B2B)',
          chartData: {
            chartType: 'bar',
            data: [
              { month: 'Month 1', DTC: 850000, B2B: 450000 },
              { month: 'Month 2', DTC: 950000, B2B: 600000 },
              { month: 'Month 3', DTC: 1050000, B2B: 620000 }
            ],
            keys: ['DTC', 'B2B']
          }
        },
        {
          type: 'list',
          title: 'Revenue Channel Breakdown',
          listData: [
            { label: 'Shopify DTC Sales', value: '₹28,50,000', extra: '70.5% share' },
            { label: 'Zoho Books B2B Sales', value: '₹16,70,000', extra: '41.3% share' },
            { label: 'Shopify Assisted Revenue', value: '-₹4,80,000', extra: 'De-duplicated' }
          ]
        },
        {
          type: 'recommendation',
          recommendationData: {
            title: 'Verify Zoho Reconciliation',
            desc: 'We recommend cross-referencing GST invoice receipts monthly to avoid duplicate ledger postings on B2B orders.',
            priority: 'medium'
          }
        }
      ];
    } else if (lowercaseMsg.includes("conversion") || lowercaseMsg.includes("funnel") || lowercaseMsg.includes("drop")) {
      botResponseText = "Nooe's Shopify DTC conversion funnel shows an overall conversion rate of 1.70%. The largest drop-off occurs from Page Views to Add to Cart (92% drop-off):";
      botWidgets = [
        {
          type: 'kpi',
          title: 'Overall Storefront Conversion',
          kpiData: { value: '1.70%', change: '+0.15% vs target', trend: 'up' }
        },
        {
          type: 'chart',
          title: 'Shopify Conversion Funnel Drop-offs',
          chartData: {
            chartType: 'funnel',
            data: [
              { name: 'Views', count: 124500, value: 100 },
              { name: 'ATC', count: 9960, value: 8 },
              { name: 'Checkout', count: 4482, value: 3.6 },
              { name: 'Purchases', count: 2120, value: 1.7 }
            ],
            keys: ['value']
          }
        },
        {
          type: 'recommendation',
          recommendationData: {
            title: 'Resolve Checkout Drop-off',
            desc: 'Nearly 52% of initiated checkouts drop off. Implement instant UPI options and pre-filled address forms to capture mobile users.',
            priority: 'high'
          }
        }
      ];
    } else if (lowercaseMsg.includes("stock") || lowercaseMsg.includes("inventory") || lowercaseMsg.includes("oos") || lowercaseMsg.includes("available")) {
      botResponseText = "Critical Warning: Our top-selling priority SKUs (A-Tier) face a heavy 28.5% stock-out rate, while our total catalog-wide stock-out sits at 16.9%:";
      botWidgets = [
        {
          type: 'kpi',
          title: 'A-Tier Stock-out Rate',
          kpiData: { value: '28.5%', change: 'Critical Risk', trend: 'down' }
        },
        {
          type: 'list',
          title: 'OOS Metrics Split',
          listData: [
            { label: 'Active Live SKUs', value: '142', extra: 'excluding archives' },
            { label: 'Available Count', value: '118', extra: '83.1% of catalog' },
            { label: 'Out-of-Stock Count', value: '24', extra: '16.9% of catalog' }
          ]
        },
        {
          type: 'recommendation',
          recommendationData: {
            title: 'Mitigate Lead Times with Suppliers',
            desc: 'Establish safety buffers of 20% on peak desk mat colors to avoid ₹3.8L of monthly lost sales.',
            priority: 'high'
          }
        }
      ];
    } else if (lowercaseMsg.includes("ads") || lowercaseMsg.includes("meta") || lowercaseMsg.includes("google") || lowercaseMsg.includes("roas") || lowercaseMsg.includes("marketing") || lowercaseMsg.includes("spend")) {
      botResponseText = "Here is the Performance Ads breakdown. Google Search Ads deliver high ROAS (4.09), but Meta commands 72% of total spend with solid video ad performance:";
      botWidgets = [
        {
          type: 'kpi',
          title: 'Blended Ads ROAS',
          kpiData: { value: '3.39', change: '+8.2% vs Q1', trend: 'up' }
        },
        {
          type: 'chart',
          title: 'Channel Efficiency (Spend vs ROAS)',
          chartData: {
            chartType: 'bar',
            data: [
              { name: 'Meta Ads', Spend: 610000, ROAS: 3.12 },
              { name: 'Google Ads', Spend: 230000, ROAS: 4.09 }
            ],
            keys: ['ROAS']
          }
        },
        {
          type: 'list',
          title: 'Top Performing Ad Creative',
          listData: [
            { label: 'Ad #1 "Organized Desk Setup" (Video)', value: '4.85 ROAS', extra: 'CPP ₹420' },
            { label: 'Ad #2 "Folio Sleeve Lifestyle" (Carousel)', value: '3.60 ROAS', extra: 'CPP ₹510' }
          ]
        },
        {
          type: 'recommendation',
          recommendationData: {
            title: 'Scale Video Creative',
            desc: 'Allocate 15% more budget from Google generic search to Meta video asset testing as video formats show 2.1x CTR.',
            priority: 'medium'
          }
        }
      ];
    } else if (lowercaseMsg.includes("owner") || lowercaseMsg.includes("neetica") || lowercaseMsg.includes("trisha") || lowercaseMsg.includes("portfolio")) {
      botResponseText = "Product performance and catalog ownership is split between Neetica and Trisha. Neetica leads in total sales driven by Desk Mats & Sleeves:";
      botWidgets = [
        {
          type: 'chart',
          title: 'Category Sales Split (₹)',
          chartData: {
            chartType: 'pie',
            data: [
              { name: 'Neetica (Desk Mats / Sleeves)', value: 2040000 },
              { name: 'Trisha (Tech Organizers)', value: 810000 }
            ],
            keys: ['value']
          }
        },
        {
          type: 'list',
          title: 'Catalog Split Details',
          listData: [
            { label: 'Neetica Portfolio Share', value: '68% of sales', extra: '98 active SKUs' },
            { label: 'Trisha Portfolio Share', value: '32% of sales', extra: '44 active SKUs' }
          ]
        }
      ];
    } else if (lowercaseMsg.includes("cx") || lowercaseMsg.includes("escalation") || lowercaseMsg.includes("automation") || lowercaseMsg.includes("csat") || lowercaseMsg.includes("delivery")) {
      botResponseText = "While CSAT remains high at 92%, manual tracking of delivery and cart recoveries are prime candidates for webhook automation:";
      botWidgets = [
        {
          type: 'kpi',
          title: 'CSAT Customer Satisfaction',
          kpiData: { value: '92.0%', change: 'Pragatee (Owner)', trend: 'up' }
        },
        {
          type: 'recommendation',
          recommendationData: {
            title: 'Automate Delivery Webhooks',
            desc: 'Migrate the 34 weekly manual delivery escalations to automatic Shiprocket carrier-tracking triggers.',
            priority: 'high'
          }
        },
        {
          type: 'list',
          title: 'Weekly Escalations Log',
          listData: [
            { label: 'Abandoned Carts (Asana)', value: '480 carts', extra: '12.5% recovery rate' },
            { label: 'Product Escalations (Return Prime)', value: '18 cases', extra: 'Stitching pouch issues' },
            { label: 'Delivery Escalations (Manual)', value: '34 cases', extra: 'Needs urgent automation' }
          ]
        }
      ];
    } else {
      // Intelligent fallback for custom prompt
      const mockVal = Math.floor(Math.random() * 80) + 15;
      botResponseText = `I have parsed your custom query through Nooe's spreadsheet ledger instantly. Dynamic operational forecasting data generated:`;
      botWidgets = [
        {
          type: 'kpi',
          title: 'Dynamic Simulation Output',
          kpiData: { value: `${mockVal}% Efficiency`, change: 'Optimal Range', trend: 'up' }
        },
        {
          type: 'recommendation',
          recommendationData: {
            title: 'Adaptive Data Synthesis',
            desc: `This query represents custom operational tracking coordinates. The visual intelligence board organizes these parameters on-the-fly.`,
            priority: 'medium'
          }
        },
        {
          type: 'list',
          title: 'Inferred Pipeline Metrics',
          listData: [
            { label: 'Dynamic Request ID', value: `RE-${Math.floor(Math.random() * 9000 + 1000)}` },
            { label: 'Confidence Threshold', value: '98.5%' },
            { label: 'Inferred Category', value: 'Operational Forecast' }
          ]
        }
      ];
    }

    const botMsgId = Math.random().toString();
    const botMsg: ChatMessage = {
      id: botMsgId,
      sender: 'bot',
      text: botResponseText,
      widgets: botWidgets,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg, botMsg]);
    setInput('');
    setActiveMessageId(botMsgId);
  };

  const activeMessage = messages.find(m => m.id === activeMessageId) || messages[0];

  // Helper to render customized super-beautiful interactive visual boards
  const renderBeautifulCustomBoard = () => {
    const lowercase = activeMessage.text.toLowerCase();

    // 1. Welcome Onboarding screen
    if (activeMessage.id === 'welcome') {
      return (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 rounded-3xl text-white shadow-md relative overflow-hidden border border-slate-700/50">
            <div className="absolute top-[-40%] right-[-10%] w-60 h-60 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="h-5 w-5 text-white animate-pulse" />
              </div>
              <h3 className="text-lg font-bold font-arial tracking-wide">Nooe Executive Briefing Board</h3>
            </div>
            <p className="text-xs text-slate-200 leading-relaxed max-w-xl font-medium">
              Welcome to the centralized decision cockpit. This board displays real-time, de-duplicated ledger entries across DTC storefronts, B2B pipelines, live inventory logs, marketing ROAS, and customer support automations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200/80 p-4.5 rounded-2xl shadow-xs">
              <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Combined Sales</span>
              <p className="text-2xl font-mono font-bold text-slate-800 mt-1">₹40.4L</p>
              <span className="text-[10px] text-emerald-600 font-mono font-bold block mt-0.5">+14% MoM growth</span>
            </div>
            <div className="bg-white border border-slate-200/80 p-4.5 rounded-2xl shadow-xs">
              <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Blended ROAS</span>
              <p className="text-2xl font-mono font-bold text-slate-800 mt-1">3.39</p>
              <span className="text-[10px] text-emerald-600 font-mono font-bold block mt-0.5">Optimal margin band</span>
            </div>
            <div className="bg-white border border-slate-200/80 p-4.5 rounded-2xl shadow-xs">
              <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Storefront CR</span>
              <p className="text-2xl font-mono font-bold text-slate-800 mt-1">1.70%</p>
              <span className="text-[10px] text-orange-500 font-mono font-bold block mt-0.5">High cart drop-off</span>
            </div>
            <div className="bg-white border border-slate-200/80 p-4.5 rounded-2xl shadow-xs">
              <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">CX Satisfaction</span>
              <p className="text-2xl font-mono font-bold text-slate-800 mt-1">92%</p>
              <span className="text-[10px] text-emerald-600 font-mono font-bold block mt-0.5">Excellent score</span>
            </div>
          </div>

          <div className="bg-orange-50/50 border border-orange-100 p-5 rounded-2xl space-y-3 shadow-xs">
            <div className="flex items-center gap-2 text-orange-800">
              <Lightbulb className="h-5 w-5 text-orange-500" />
              <span className="text-xs uppercase font-mono tracking-wider font-bold">Suggested Diagnostic Flows</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed font-sans">
              Get detailed visual briefs on specific operational pillars by submitting queries such as:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] font-medium text-slate-700">
              <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-slate-200/60">
                <span className="text-orange-500 font-bold font-mono">01.</span>
                <span>"Show combined Q2 sales and de-duplication ledger"</span>
              </div>
              <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-slate-200/60">
                <span className="text-orange-500 font-bold font-mono">02.</span>
                <span>"Why is storefront conversion dropping?"</span>
              </div>
              <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-slate-200/60">
                <span className="text-orange-500 font-bold font-mono">03.</span>
                <span>"Which products are currently out-of-stock?"</span>
              </div>
              <div className="flex items-center gap-2 bg-white/80 p-2.5 rounded-xl border border-slate-200/60">
                <span className="text-orange-500 font-bold font-mono">04.</span>
                <span>"Break down Meta vs Google Ads performance"</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 2. Q2 Combined Revenue & Reconciliation Ledger Infographic
    if (lowercase.includes("revenue") || lowercase.includes("sales") || lowercase.includes("combined") || lowercase.includes("de-duplicate")) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            
            {/* Visual Accounting Ledger Card */}
            <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm lg:col-span-2 flex flex-col justify-between space-y-4">
              <div>
                <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                  <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Audited Reconciliation Ledger</span>
                  <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full border border-emerald-100 uppercase font-bold">RECONCILED</span>
                </div>
                
                {/* Ledger Infographic Items */}
                <div className="mt-4 space-y-3 font-sans">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="h-6 w-6 rounded-md bg-slate-200/60 flex items-center justify-center font-mono text-[10px] font-bold text-slate-600">A</div>
                      <span className="text-xs font-semibold text-slate-700">Gross Shopify DTC Sales</span>
                    </div>
                    <span className="text-xs font-mono font-bold text-slate-800">+ ₹28,50,000</span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="h-6 w-6 rounded-md bg-slate-200/60 flex items-center justify-center font-mono text-[10px] font-bold text-slate-600">B</div>
                      <span className="text-xs font-semibold text-slate-700">Zoho Books B2B Accounts</span>
                    </div>
                    <span className="text-xs font-mono font-bold text-slate-800">+ ₹16,70,000</span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-orange-50/40 rounded-xl border border-orange-100">
                    <div className="flex items-center gap-2.5">
                      <div className="h-6 w-6 rounded-md bg-orange-100/60 flex items-center justify-center font-mono text-[10px] font-bold text-orange-600">C</div>
                      <span className="text-xs font-semibold text-slate-700">Assisted Shopify Orders Duplicate</span>
                    </div>
                    <span className="text-xs font-mono font-bold text-orange-600">- ₹4,80,000</span>
                  </div>

                  <div className="pt-2 flex justify-between items-center border-t-2 border-dashed border-slate-200 px-1 mt-2">
                    <span className="text-sm font-bold text-slate-800">Net Reconciled Revenue (A + B - C)</span>
                    <span className="text-base font-mono font-bold text-orange-500 bg-orange-50 px-3 py-1 rounded-lg border border-orange-100">₹40,40,000</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2.5 bg-slate-50 p-3 rounded-2xl text-[10.5px] leading-relaxed text-slate-500 font-medium">
                <Info className="h-4.5 w-4.5 text-slate-400 shrink-0 mt-0.5" />
                <span>Shopify assisted sales were overlapping with Zoho wholesale logs. Removing this duplicates prevents double taxation reporting on ₹4.8L of entries.</span>
              </div>
            </div>

            {/* Quick KPI stats */}
            <div className="space-y-4 flex flex-col">
              <div className="bg-white border border-slate-200/80 p-5 rounded-2xl flex-1 flex flex-col justify-center space-y-2.5 shadow-sm">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">DTC vs B2B Share</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-mono font-bold text-slate-800">70.5%</span>
                  <span className="text-xs font-sans text-slate-500 font-semibold">DTC Share</span>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden flex">
                  <div className="bg-orange-500 h-full" style={{ width: '70.5%' }} />
                  <div className="bg-blue-500 h-full" style={{ width: '29.5%' }} />
                </div>
                <span className="text-[9.5px] text-slate-400 font-medium block">DTC: ₹28.5L | B2B: ₹16.7L</span>
              </div>

              <div className="bg-white border border-slate-200/80 p-5 rounded-2xl flex-1 flex flex-col justify-center space-y-1 shadow-sm">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Audit Status</span>
                <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                  <CheckCircle className="h-4.5 w-4.5 text-emerald-500" />
                  <span>Verified Reconciled</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Cross-reconciliation of 24B ledger codes complete.</p>
              </div>
            </div>
          </div>

          {/* Chart Component */}
          <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Monthly Channels Trend Comparison (₹)</span>
            <div className="h-56 w-full text-slate-600">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { month: 'April (Month 1)', DTC: 850000, B2B: 450000 },
                  { month: 'May (Month 2)', DTC: 950000, B2B: 600000 },
                  { month: 'June (Month 3)', DTC: 1050000, B2B: 620000 }
                ]} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(0,0,0,0.03)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={10} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} />
                  <ChartTooltip 
                    contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)' }} 
                    labelStyle={{ fontSize: '10px', color: '#1e293b', fontWeight: 'bold' }}
                  />
                  <Bar dataKey="DTC" fill="#ff7a59" radius={[4, 4, 0, 0]} name="Shopify DTC Sales" />
                  <Bar dataKey="B2B" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Zoho Wholesale B2B" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    }

    // 3. Storefront Conversion & Funnel Friction points
    if (lowercase.includes("conversion") || lowercase.includes("funnel") || lowercase.includes("drop")) {
      return (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">DTC Storefront Funnel Leakage</span>
              <span className="text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-100 font-mono font-bold">1.70% overall cr</span>
            </div>

            {/* Immersive Conversion Journey Steps (Friction points mapped) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
              <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl relative">
                <div className="absolute top-3 right-3 text-[10px] font-mono font-bold text-slate-300">STAGE 1</div>
                <p className="text-[11px] font-mono text-slate-400 font-semibold uppercase tracking-wider">Page Views</p>
                <p className="text-xl font-mono font-bold text-slate-800 mt-1">1,24,500</p>
                <div className="text-[9.5px] text-emerald-600 mt-2 font-medium flex items-center gap-1">
                  <span className="h-1 w-1 bg-emerald-500 rounded-full"></span>
                  100% of visitors
                </div>
                <p className="text-[10px] text-slate-500 mt-2.5 leading-relaxed font-sans border-t border-slate-200/55 pt-2">
                  Traffic from Meta Video Campaigns & Organic Instagram. Page load speed is highly optimal (1.4s).
                </p>
              </div>

              <div className="bg-orange-50/20 border border-orange-100/60 p-4 rounded-2xl relative">
                <div className="absolute top-3 right-3 text-[10px] font-mono font-bold text-orange-300">STAGE 2</div>
                <p className="text-[11px] font-mono text-orange-600/80 font-semibold uppercase tracking-wider">Add To Cart</p>
                <p className="text-xl font-mono font-bold text-slate-800 mt-1">9,960</p>
                <div className="text-[9.5px] text-orange-600 mt-2 font-bold flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  8.0% (92% Drop-off)
                </div>
                <p className="text-[10px] text-slate-600 mt-2.5 leading-relaxed font-sans border-t border-orange-100/50 pt-2 font-medium">
                  ⚠️ Severe friction: product pages lack high-resolution video guides, zoom grids, and clear accessory sizing maps.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl relative">
                <div className="absolute top-3 right-3 text-[10px] font-mono font-bold text-slate-300">STAGE 3</div>
                <p className="text-[11px] font-mono text-slate-400 font-semibold uppercase tracking-wider">Initiate Checkout</p>
                <p className="text-xl font-mono font-bold text-slate-800 mt-1">4,482</p>
                <div className="text-[9.5px] text-orange-600 mt-2 font-bold flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  3.6% (55% Drop-off)
                </div>
                <p className="text-[10px] text-slate-500 mt-2.5 leading-relaxed font-sans border-t border-slate-200/55 pt-2">
                  ⚠️ Form friction: address pre-filling is missing, causing checkout fatigue. Shipping options are too rigid.
                </p>
              </div>

              <div className="bg-white border border-slate-200 p-4 rounded-2xl relative shadow-xs">
                <div className="absolute top-3 right-3 text-[10px] font-mono font-bold text-slate-300">STAGE 4</div>
                <p className="text-[11px] font-mono text-slate-400 font-semibold uppercase tracking-wider">Completed Purchases</p>
                <p className="text-xl font-mono font-bold text-emerald-600 mt-1">2,120</p>
                <div className="text-[9.5px] text-emerald-600 mt-2 font-bold flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  1.70% (52% checkout CR)
                </div>
                <p className="text-[10px] text-slate-500 mt-2.5 leading-relaxed font-sans border-t border-slate-200/55 pt-2">
                  Completed sales driven primarily by mobile users. UPI represents 64% of payments completed.
                </p>
              </div>
            </div>
          </div>

          {/* Action Optimization Plan */}
          <div className="bg-orange-50/50 border border-orange-100 p-5 rounded-2xl space-y-3 shadow-xs">
            <div className="flex items-center gap-2 text-orange-800">
              <Lightbulb className="h-5 w-5 text-orange-500" />
              <span className="text-xs uppercase font-mono tracking-wider font-bold">Optimization Action Plan</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="space-y-1">
                <span className="font-bold text-slate-800 block">1. Implement Quick UPI Gateway</span>
                <p className="text-slate-500 leading-relaxed text-[11px]">
                  Integrate Razorpay or Cashfree Quick UPI buttons directly on the Add to Cart page, bypassing 3 billing form fields. Expected CR lift: +18%.
                </p>
              </div>
              <div className="space-y-1">
                <span className="font-bold text-slate-800 block">2. Enhance Product Media</span>
                <p className="text-slate-500 leading-relaxed text-[11px]">
                  Add 15-second product walk-through videos on Desk Mat color variants. Desk Mat buyers have a high add-to-cart rate when video is present.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 4. Stock-out & Inventory Out-of-Stock List & Revenue Impact
    if (lowercase.includes("stock") || lowercase.includes("inventory") || lowercase.includes("oos") || lowercase.includes("available")) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            
            {/* OOS SKUs detailed Table */}
            <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Active OOS Priority Bestsellers</span>
                <span className="text-[9px] font-mono bg-red-50 text-red-600 px-2.5 py-0.5 rounded border border-red-100 uppercase font-bold">CRITICAL DEFICIT</span>
              </div>

              <div className="divide-y divide-slate-100 text-xs">
                <div className="flex justify-between py-3 font-semibold text-slate-400 font-mono text-[10.5px]">
                  <span>PRODUCT SKU NAME</span>
                  <div className="flex gap-8">
                    <span>DAYS OOS</span>
                    <span>FORECASTED MONTHLY LOSS</span>
                  </div>
                </div>

                <div className="flex justify-between py-3 items-center">
                  <div className="space-y-0.5">
                    <span className="font-bold text-slate-800 block">Charcoal Felt Desk Mat (Large)</span>
                    <span className="text-[10.5px] text-slate-400 font-mono">SKU: DM-FL-CHAR-LG</span>
                  </div>
                  <div className="flex gap-14 items-center">
                    <span className="font-bold text-red-500 font-mono">8 Days</span>
                    <span className="font-bold text-slate-800 font-mono">₹1.40L</span>
                  </div>
                </div>

                <div className="flex justify-between py-3 items-center">
                  <div className="space-y-0.5">
                    <span className="font-bold text-slate-800 block">Sand Leather Sleeve (MacBook 14")</span>
                    <span className="text-[10.5px] text-slate-400 font-mono">SKU: LS-LTH-SND-14</span>
                  </div>
                  <div className="flex gap-12 items-center">
                    <span className="font-bold text-red-500 font-mono">12 Days</span>
                    <span className="font-bold text-slate-800 font-mono">₹1.10L</span>
                  </div>
                </div>

                <div className="flex justify-between py-3 items-center">
                  <div className="space-y-0.5">
                    <span className="font-bold text-slate-800 block">Sage Travel Tech Pouch</span>
                    <span className="text-[10.5px] text-slate-400 font-mono">SKU: TP-SGE-TRAVEL</span>
                  </div>
                  <div className="flex gap-14 items-center">
                    <span className="font-bold text-red-500 font-mono">4 Days</span>
                    <span className="font-bold text-slate-800 font-mono">₹0.80L</span>
                  </div>
                </div>

                <div className="flex justify-between py-3 items-center">
                  <div className="space-y-0.5">
                    <span className="font-bold text-slate-800 block">Sienna Folio iPad Cover (11")</span>
                    <span className="text-[10.5px] text-slate-400 font-mono">SKU: FC-IPD-SIA-11</span>
                  </div>
                  <div className="flex gap-14 items-center">
                    <span className="font-bold text-red-500 font-mono">6 Days</span>
                    <span className="font-bold text-slate-800 font-mono">₹0.50L</span>
                  </div>
                </div>
              </div>
            </div>

            {/* OOS Stats and Supply */}
            <div className="space-y-4 flex flex-col justify-between">
              <div className="bg-white border border-slate-200/80 p-5 rounded-2xl flex-1 flex flex-col justify-center space-y-1.5 shadow-sm">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Forecasted Sales Gap</span>
                <p className="text-3xl font-mono font-bold text-red-500">₹3.80L</p>
                <span className="text-[10.5px] text-slate-500 font-semibold leading-tight block">Total monthly revenue currently lost due to supply pipeline delays.</span>
              </div>

              <div className="bg-white border border-slate-200/80 p-5 rounded-2xl flex-1 flex flex-col justify-center space-y-2 shadow-sm">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Supplier Performance</span>
                <div className="space-y-1.5 text-[11px] font-medium text-slate-600">
                  <div className="flex justify-between">
                    <span>Supplier Alpha (Delhi):</span>
                    <span className="font-mono font-bold text-slate-800">14d lead</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Supplier Beta (Channai):</span>
                    <span className="font-mono font-bold text-slate-800">28d lead</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-100 pt-1">
                    <span className="text-red-500">Active Stock-out:</span>
                    <span className="font-mono font-bold text-red-500">28.5% A-Tier</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Supplier Optimization advice */}
          <div className="bg-red-50/40 border border-red-100 p-5 rounded-2xl space-y-3">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="text-xs uppercase font-mono tracking-wider font-bold">Supply Chain Replenishment Directives</span>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed font-sans">
              To mitigate stock-outs on charcoal and sand variants, raise supplier order safety thresholds by 20% by the 25th of each month. Deliver stock batches directly to the main Delhi fulfillment center to shave 4 days off regional transit lead times.
            </p>
          </div>
        </div>
      );
    }

    // 5. Performance Ads & ROAS Spend Efficiency Matrix
    if (lowercase.includes("ads") || lowercase.includes("meta") || lowercase.includes("google") || lowercase.includes("roas") || lowercase.includes("marketing") || lowercase.includes("spend")) {
      return (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Performance Marketing Channels Efficiency</span>
              <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded border border-emerald-100 uppercase font-bold">3.39 blended roas</span>
            </div>

            {/* Spend vs ROAS compare matrix */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col justify-between space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-800">Meta Video & Carousel Ads</span>
                  <span className="text-[10px] font-mono font-bold text-slate-400">72.6% SPEND</span>
                </div>
                <div className="flex justify-between items-baseline pt-2">
                  <div className="space-y-0.5">
                    <span className="text-xs text-slate-400 block">Total Spend</span>
                    <span className="text-xl font-mono font-bold text-slate-800">₹6,10,000</span>
                  </div>
                  <div className="text-right space-y-0.5">
                    <span className="text-xs text-slate-400 block">Performance</span>
                    <span className="text-xl font-mono font-bold text-orange-500">3.12 ROAS</span>
                  </div>
                </div>
                <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden mt-2">
                  <div className="bg-orange-500 h-full" style={{ width: '72%' }} />
                </div>
                <span className="text-[9.5px] text-slate-400 block mt-1">Video creatives account for 64% of checkout conversions.</span>
              </div>

              <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col justify-between space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-800">Google Search & Performance Max</span>
                  <span className="text-[10px] font-mono font-bold text-slate-400">27.4% SPEND</span>
                </div>
                <div className="flex justify-between items-baseline pt-2">
                  <div className="space-y-0.5">
                    <span className="text-xs text-slate-400 block">Total Spend</span>
                    <span className="text-xl font-mono font-bold text-slate-800">₹2,30,000</span>
                  </div>
                  <div className="text-right space-y-0.5">
                    <span className="text-xs text-slate-400 block">Performance</span>
                    <span className="text-xl font-mono font-bold text-emerald-600">4.09 ROAS</span>
                  </div>
                </div>
                <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden mt-2">
                  <div className="bg-blue-500 h-full" style={{ width: '27%' }} />
                </div>
                <span className="text-[9.5px] text-slate-400 block mt-1">High conversion efficiency but volume is capped by active keyword search scale.</span>
              </div>
            </div>
          </div>

          {/* High Fidelity Creative Performance Showcase */}
          <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Top Performing Creative Assets</span>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              <div className="border border-slate-200/80 p-3 rounded-2xl bg-white space-y-2">
                <img 
                  src="https://images.unsplash.com/photo-1585776245991-cf89dd7fc73a?q=80&w=250&auto=format&fit=crop" 
                  alt="Creative" 
                  className="w-full aspect-[4/3] object-cover rounded-xl"
                  referrerPolicy="no-referrer"
                />
                <div className="flex justify-between items-start pt-1">
                  <div>
                    <span className="text-[10.5px] font-bold text-slate-800 block truncate">"Organized Desk" (Video)</span>
                    <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 px-1.5 py-0.2 rounded font-bold">SCALE BUDGET</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-slate-800 shrink-0">4.85 ROAS</span>
                </div>
                <div className="flex justify-between text-[9px] text-slate-400 font-mono border-t border-slate-100 pt-1.5">
                  <span>CTR: 2.4%</span>
                  <span>CPP: ₹420</span>
                </div>
              </div>

              <div className="border border-slate-200/80 p-3 rounded-2xl bg-white space-y-2">
                <img 
                  src="https://images.unsplash.com/photo-1622560480605-d83c853bc5c3?q=80&w=250&auto=format&fit=crop" 
                  alt="Creative" 
                  className="w-full aspect-[4/3] object-cover rounded-xl"
                  referrerPolicy="no-referrer"
                />
                <div className="flex justify-between items-start pt-1">
                  <div>
                    <span className="text-[10.5px] font-bold text-slate-800 block truncate">"Folio Pouch Lifestyle"</span>
                    <span className="text-[9px] font-mono bg-slate-50 text-slate-600 px-1.5 py-0.2 rounded font-bold">MAINTAIN SPEND</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-slate-800 shrink-0">3.60 ROAS</span>
                </div>
                <div className="flex justify-between text-[9px] text-slate-400 font-mono border-t border-slate-100 pt-1.5">
                  <span>CTR: 1.8%</span>
                  <span>CPP: ₹510</span>
                </div>
              </div>

              <div className="border border-slate-200/80 p-3 rounded-2xl bg-white space-y-2">
                <img 
                  src="https://images.unsplash.com/photo-1618477388954-7852f32655ec?q=80&w=250&auto=format&fit=crop" 
                  alt="Creative" 
                  className="w-full aspect-[4/3] object-cover rounded-xl"
                  referrerPolicy="no-referrer"
                />
                <div className="flex justify-between items-start pt-1">
                  <div>
                    <span className="text-[10.5px] font-bold text-slate-800 block truncate">"Accessories Flatlay" (Static)</span>
                    <span className="text-[9px] font-mono bg-red-50 text-red-700 px-1.5 py-0.2 rounded font-bold">PAUSE AD</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-red-500 shrink-0">1.90 ROAS</span>
                </div>
                <div className="flex justify-between text-[9px] text-slate-400 font-mono border-t border-slate-100 pt-1.5">
                  <span>CTR: 0.9%</span>
                  <span>CPP: ₹780</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      );
    }

    // 6. Owner Portfolio Split (Neetica vs Trisha categories)
    if (lowercase.includes("owner") || lowercase.includes("neetica") || lowercase.includes("trisha") || lowercase.includes("portfolio")) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Neetica's side */}
            <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
              <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
                <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xs">N</div>
                <div>
                  <span className="text-xs font-bold text-slate-800 block">Neetica Portfolio</span>
                  <span className="text-[9px] text-slate-400 font-mono uppercase font-bold block">68% SALES SHARE</span>
                </div>
              </div>
              <div className="space-y-3 text-xs leading-relaxed">
                <p className="text-slate-600">
                  Directs the flagship high-volume categories: **Desk Mats & Laptop Sleeves**.
                </p>
                <div className="bg-slate-50 p-3 rounded-xl space-y-2 border border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sales Value Driven:</span>
                    <span className="font-mono font-bold text-slate-800">₹20.40L</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Active Listing Count:</span>
                    <span className="font-mono font-bold text-slate-800">98 active SKUs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Average Profit Margin:</span>
                    <span className="font-mono font-bold text-emerald-600">64% margin</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trisha's side */}
            <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
              <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">T</div>
                <div>
                  <span className="text-xs font-bold text-slate-800 block">Trisha Portfolio</span>
                  <span className="text-[9px] text-slate-400 font-mono uppercase font-bold block">32% SALES SHARE</span>
                </div>
              </div>
              <div className="space-y-3 text-xs leading-relaxed">
                <p className="text-slate-600">
                  Directs the premium growth & accessory categories: **Tech Organizers & Travel Pouches**.
                </p>
                <div className="bg-slate-50 p-3 rounded-xl space-y-2 border border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Sales Value Driven:</span>
                    <span className="font-mono font-bold text-slate-800">₹8.10L</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Active Listing Count:</span>
                    <span className="font-mono font-bold text-slate-800">44 active SKUs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Average Profit Margin:</span>
                    <span className="font-mono font-bold text-emerald-600">58% margin</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Portfolio Contribution Split (₹)</span>
            <div className="h-44 w-full text-slate-600 flex justify-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "Neetica's Portfolio", value: 2040000 },
                      { name: "Trisha's Portfolio", value: 810000 }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={6}
                    dataKey="value"
                  >
                    <Cell fill="#ff7a59" />
                    <Cell fill="#3b82f6" />
                  </Pie>
                  <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    }

    // 7. CX & Webhook Automations
    if (lowercase.includes("cx") || lowercase.includes("escalation") || lowercase.includes("automation") || lowercase.includes("csat") || lowercase.includes("delivery")) {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            
            {/* Pipeline automation flowchart widget */}
            <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Automation Pipeline Visualizer</span>
                <span className="text-[9px] font-mono bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded border border-emerald-100 uppercase font-bold">WEBHOOK READY</span>
              </div>

              {/* Webhook Process Flow Diagram */}
              <div className="space-y-4 font-sans text-xs pt-1">
                <div className="flex flex-col md:flex-row items-center justify-between gap-2.5 p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="text-center md:text-left">
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Trigger Event</span>
                    <span className="font-bold text-slate-800">Shopify Checkout Initiated</span>
                  </div>
                  <div className="h-0.5 w-8 bg-slate-200 hidden md:block" />
                  <div className="text-center">
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Automation Hook</span>
                    <span className="font-bold text-orange-500 bg-orange-50 px-2.5 py-0.5 rounded border border-orange-100">Zapier / Webhook</span>
                  </div>
                  <div className="h-0.5 w-8 bg-slate-200 hidden md:block" />
                  <div className="text-center md:text-right">
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Action Result</span>
                    <span className="font-bold text-slate-800">Asana Cart Log Created</span>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between gap-2.5 p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="text-center md:text-left">
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Trigger Event</span>
                    <span className="font-bold text-slate-800">Shiprocket Carrier Status Update</span>
                  </div>
                  <div className="h-0.5 w-8 bg-slate-200 hidden md:block" />
                  <div className="text-center">
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Automation Hook</span>
                    <span className="font-bold text-red-500 bg-red-50 px-2.5 py-0.5 rounded border border-red-100">MANUAL PIPELINE</span>
                  </div>
                  <div className="h-0.5 w-8 bg-slate-200 hidden md:block" />
                  <div className="text-center md:text-right">
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">Action Result</span>
                    <span className="font-bold text-slate-800">34 manual WhatsApp pings</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick KPI stats */}
            <div className="space-y-4 flex flex-col justify-between">
              <div className="bg-white border border-slate-200/80 p-5 rounded-2xl flex-1 flex flex-col justify-center space-y-1.5 shadow-sm">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Fulfillment Delay</span>
                <p className="text-3xl font-mono font-bold text-orange-500">34 cases/wk</p>
                <span className="text-[10px] text-slate-400 font-medium leading-tight block">Manual shipping escalations logged weekly on average.</span>
              </div>

              <div className="bg-white border border-slate-200/80 p-5 rounded-2xl flex-1 flex flex-col justify-center space-y-1 shadow-sm">
                <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Support CSAT Score</span>
                <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                  <CheckCircle className="h-4.5 w-4.5 text-emerald-500" />
                  <span>92.0% (Pragatee)</span>
                </div>
                <p className="text-[9.5px] text-slate-400 mt-1">Excellent support response loops maintained.</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200/80 p-5 rounded-3xl shadow-sm space-y-4">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">Weekly Support Escalations Breakdown</span>
            <div className="divide-y divide-slate-100 text-xs">
              <div className="flex justify-between py-2.5 items-center">
                <span className="text-slate-600 font-semibold">Abandoned Shopify Carts (Asana)</span>
                <div className="text-right">
                  <span className="font-bold text-slate-800 font-mono">480 Carts Logged</span>
                  <span className="text-[9.5px] text-slate-400 block font-mono">12.5% recovered with pings</span>
                </div>
              </div>
              <div className="flex justify-between py-2.5 items-center">
                <span className="text-slate-600 font-semibold">Product stitching defects (Return Prime)</span>
                <div className="text-right">
                  <span className="font-bold text-slate-800 font-mono">18 Claims</span>
                  <span className="text-[9.5px] text-slate-400 block font-mono">Drawn from Tech Pouch variants</span>
                </div>
              </div>
              <div className="flex justify-between py-2.5 items-center">
                <span className="text-slate-600 font-semibold">Manual Carrier Escalations (Shiprocket)</span>
                <div className="text-right">
                  <span className="font-bold text-red-500 font-mono">34 Cases Logged</span>
                  <span className="text-[9.5px] text-orange-500 block font-mono">Prime candidate for webhook automation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Default Fallback / custom request
    return (
      <div className="space-y-5">
        <div className="bg-white border border-slate-200/80 p-5 rounded-2xl flex flex-col justify-between space-y-2.5 shadow-sm">
          <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">
            Dynamic Intelligence Analysis
          </span>
          <div className="flex items-baseline justify-between">
            <span className="text-3xl font-mono font-bold text-slate-800 tracking-tight">
              Inferred Operational Forecast
            </span>
            <span className="text-xs font-mono font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">
              98.5% Confidence
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200/80 p-5 rounded-2xl space-y-3 shadow-sm">
            <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block">
              Inferred Pipeline Metrics
            </span>
            <div className="divide-y divide-slate-100 text-xs">
              <div className="flex justify-between py-2.5">
                <span className="text-slate-500">Dynamic Request ID:</span>
                <span className="font-mono font-bold text-slate-800">RE-{Math.floor(Math.random() * 9000 + 1000)}</span>
              </div>
              <div className="flex justify-between py-2.5">
                <span className="text-slate-500">Analysis Mode:</span>
                <span className="font-mono font-bold text-slate-800">Zero-Latency Synthesis</span>
              </div>
              <div className="flex justify-between py-2.5">
                <span className="text-slate-500">Status:</span>
                <span className="font-mono font-bold text-emerald-600">Simulated Success</span>
              </div>
            </div>
          </div>

          <div className="bg-orange-50/50 border border-orange-100 p-5 rounded-2xl space-y-2.5 shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-mono uppercase bg-orange-100 text-orange-700 px-2.5 py-0.5 rounded-full font-bold">
                PROTOTYPE DIRECTIVE
              </span>
              <Lightbulb className="h-4 w-4 text-orange-500" />
            </div>
            <h4 className="text-sm font-bold text-slate-800 leading-tight">Adaptive Data Projection</h4>
            <p className="text-xs text-slate-600 leading-relaxed font-sans">
              This intelligence board adaptively projects spreadsheets and ledger balances. Formulate your specific metrics questions above to parse the ledger.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Get active followups list (always returns exactly 5 recommended prompts)
  const activeFollowUps = getFollowUpQuestions(activeMessage.text);

  return (
    <>
      {/* Floating trigger button contained in app */}
      <button
        id="chat-trigger"
        data-testid="chatbot-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
        className="absolute bottom-6 right-6 h-14 w-14 rounded-full bg-[#1e3a8a] text-white flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-all duration-300 z-50 group border border-blue-800/35"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <div className="relative">
            <MessageSquare className="h-6 w-6 animate-pulse" />
            <span className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 bg-white rounded-full border border-[#1e3a8a] flex items-center justify-center">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            </span>
          </div>
        )}
      </button>

      {/* Spacious Centered Glassmorphic Workspace overlay inside dashboard canvas */}
      {isOpen && (
        <div 
          id="chat-screen"
          className="absolute inset-4 bg-[#f1f5f9]/95 backdrop-blur-3xl border border-white/80 rounded-[2rem] shadow-[0_32px_100px_rgba(15,23,42,0.18)] flex flex-col md:flex-row overflow-hidden z-40 animate-fade-in font-sans text-slate-800"
        >
          {/* Left Column: Conversational Thread (Frosted Off-White Sidebar, Width 430px) */}
          <div className="w-full md:w-[400px] lg:w-[430px] border-r border-slate-200/60 flex flex-col justify-between bg-slate-50/50 shrink-0 h-full min-h-0">
            
            {/* Header Area */}
            <div className="p-4 border-b border-slate-200/60 bg-[#f1f5f9]/40 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="h-8.5 w-8.5 rounded-xl bg-orange-500 flex items-center justify-center shadow-md shadow-orange-500/25">
                  <Sparkles className="h-4.5 w-4.5 text-white animate-pulse" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Nooe Copilot</h3>
                  <span className="text-[9px] font-mono text-emerald-600 uppercase tracking-widest flex items-center gap-1 font-bold">
                    <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full animate-ping"></span>
                    Instant Analyzer
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-mono font-bold uppercase bg-slate-100 border border-slate-200 text-slate-500 px-2 py-0.5 rounded">
                  PROTOTYPE
                </span>
              </div>
            </div>

            {/* Conversation Messages Box */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0 bg-[#f8fafc]/30">
              {messages.map((m) => {
                const isSelected = activeMessageId === m.id;
                return (
                  <div 
                    key={m.id} 
                    onClick={() => { if (m.widgets && m.widgets.length > 0) setActiveMessageId(m.id); }}
                    className={`flex flex-col max-w-[92%] transition-all ${
                      m.sender === 'user' ? 'ml-auto items-end cursor-pointer' : 'mr-auto items-start cursor-pointer'
                    }`}
                  >
                    {/* Chat bubble text */}
                    <div 
                      className={`p-3 rounded-2xl text-[11.5px] font-sans leading-relaxed transition-all ${
                        m.sender === 'user' 
                          ? 'bg-orange-500 text-white font-medium rounded-tr-none shadow-md shadow-orange-500/10 hover:bg-orange-600' 
                          : `border text-slate-700 rounded-tl-none ${
                              isSelected 
                                ? 'bg-white border-orange-400 shadow-sm shadow-orange-500/5 text-slate-800 font-medium' 
                                : 'bg-slate-100/70 border-slate-200/60 hover:bg-white hover:border-slate-300'
                            }`
                      }`}
                    >
                      {m.text}
                      {m.widgets && m.widgets.length > 0 && (
                        <div className="mt-2 flex items-center gap-1 text-[8.5px] font-mono font-bold text-orange-600 uppercase tracking-wider">
                          <Layers className="h-2.5 w-2.5" />
                          <span>{isSelected ? "Active On Board" : "Click to view board"}</span>
                        </div>
                      )}
                    </div>

                    <span className="text-[8px] font-mono text-slate-400 mt-1.5 uppercase tracking-wider font-bold">
                      {m.timestamp}
                    </span>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Dynamic suggested continuation question pills (Scrolling list, saves huge vertical height) */}
            <div className="px-4 py-3 bg-[#f1f5f9]/50 border-t border-slate-200/50 space-y-2 shrink-0">
              <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400 block font-bold flex items-center gap-1">
                <Sparkles className="h-2.5 w-2.5 text-orange-500" />
                Recommended Follow-ups
              </span>
              <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-none">
                {activeFollowUps.map((s, idx) => {
                  if (s === "Ask a custom question...") return null;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleSend(s)}
                      className="whitespace-nowrap text-[10.5px] text-slate-700 bg-white border border-slate-200 hover:border-orange-500/30 hover:bg-orange-50/40 hover:text-orange-700 px-3.5 py-2 rounded-full transition-all duration-200 shadow-3xs cursor-pointer font-medium"
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Action Bar Input Form */}
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSend(input); }}
              className="p-3 border-t border-slate-200/60 bg-white flex items-center gap-2 shrink-0"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about revenue, conversions, stock..."
                className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 focus:outline-none focus:border-orange-500/50 text-slate-800 placeholder-slate-400 transition-colors focus:bg-white"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                className={`h-9.5 w-9.5 rounded-xl flex items-center justify-center transition-all ${
                  input.trim() 
                    ? 'bg-orange-500 text-white hover:scale-105 active:scale-95 shadow-md shadow-orange-500/15' 
                    : 'bg-slate-100 text-slate-300 border border-slate-200'
                }`}
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </form>
          </div>

          {/* Right Column: High-Fidelity Light-Grey Visual Intelligence Board */}
          <div className="flex-1 bg-[#f1f5f9]/25 p-5 md:p-6 overflow-y-auto h-full flex flex-col min-h-0">
            {/* Board Header Section */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-200/60 mb-5 shrink-0">
              <div className="flex items-center gap-2 font-mono">
                <BarChart3 className="h-4.5 w-4.5 text-orange-500" />
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Visual Intelligence Board
                </span>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                title="Close Assistant"
              >
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            {/* Rendered customized super-beautiful visual boards */}
            <div className="flex-1 min-h-0">
              {renderBeautifulCustomBoard()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
